<template>
  <div
    class="fixed top-0 left-0 h-screen w-screen bg-slate-100 overflow-auto scrollbar-lg"
    :style="{ zIndex: 999 }"
  >
    <div class="sticky top-0 z-30">
      <div
        class="px-6 h-10 flex-shrink-0 flex flex-wrap lg:flex-no-wrap items-center justify-between gap-y-2 gap-x-4 bg-pure-white"
      >
        <BlBreadcrumbs :breadcrumbs="breadcrumbs" />
      </div>
      <div
        class="h-14 px-6 flex items-center justify-between bg-pure-white border-b border-slate-400"
      >
        <BlTextTitle4 text="Create a new appointment" />
        <BlInputButton
          type="plain"
          icon="close"
          @clicked="$emit('close-modal')"
        />
      </div>
    </div>

    <div class="w-full py-8 flex flex-col items-center gap-4">
      <div class="w-152 px-10 flex flex-col">
        <BlTextSubtitle1 text="Therapist details" class="mb-1" />
        <BlTextBody2
          v-if="isOpenRoom"
          text="You're about to create an appointment in an open room."
          class="mb-3"
        />
        <BlTextBody2
          v-else
          text="You're about to create an appointment from this therapist:"
          class="mb-3"
        />
        <div class="flex items-center gap-2">
          <div class="w-25 flex-shrink-0 rounded shadow-md">
            <div
              class="h-7 flex items-center justify-center rounded-t bg-orange-200"
            >
              <BlTextBody2
                :text="displayDate({ date: availability.date, format: 'dddd' })"
                class="text-orange-700"
              />
            </div>
            <div
              class="h-18 flex flex-col items-center justify-center rounded-b bg-pure-white"
            >
              <BlTextTitle4
                :text="displayDate({ date: availability.date, format: 'DD' })"
                class="text-orange-700"
              />
              <BlTextBody2
                :text="displayDate({ date: availability.date, format: 'MMMM' })"
                class="text-orange-600"
              />
            </div>
          </div>
          <div class="flex flex-col gap-1 px-2">
            <div
              class="max-w-100 flex items-center gap-2"
              :title="
                (user && user.name) || 'The therapist is not yet assigned'
              "
            >
              <BlIcon icon="user" color="slate-6" class="h-4 w-4" />
              <BlTextBody2
                v-if="!user"
                :text="'The therapist is not yet assigned'"
                class="italic truncate"
              />
              <BlTextBody2 v-else :text="user && user.name" class="truncate" />
            </div>
            <div class="flex items-center gap-2">
              <BlIcon icon="door" color="slate-6" class="h-4 w-4" />
              <div
                v-if="enableBranch && availability.room.branch"
                class="h-7 max-w-48 px-2 flex items-center justify-center rounded"
                :style="{
                  backgroundColor:
                    availability.room.branch.background_color || 'unset'
                }"
                :title="availability.room.branch.name"
              >
                <BlTextBody2
                  class="truncate"
                  :text="availability.room.branch.name || '-'"
                  :style="{
                    color: availability.room.branch.color || 'unset'
                  }"
                />
              </div>
              <div class="flex max-w-48">
                <BlTextBody2 :text="availability.room.name" class="truncate" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="w-152 py-10 px-10 flex flex-col bg-pure-white rounded-3xl border border-slate-400"
      >
        <div class="mb-3 flex flex-col gap-1">
          <BlTextSubtitle1
            v-if="isOpenRoom"
            text="Select a client, therapist, and timeslot"
          />
          <BlTextSubtitle1 v-else text="Select a client and a timeslot" />
          <BlTextBody2
            v-if="isOpenRoom"
            text="To book the open room, please fill out the information."
          />
          <BlTextBody2
            v-else
            text="Please select a client and a timeslot to assign to the therapist."
          />
        </div>
        <div class="flex flex-col gap-4">
          <div>
            <BlSelectBox
              search
              prefixIcon="user-square"
              v-model="clientInput"
              placeholder="Select client"
              :options="clientOptions"
              @change="onChangeClient"
              :searching="clientLoading"
              @search="fetchClients"
            />
          </div>
          <div v-if="isOpenRoom">
            <BlSelectBox
              :key="
                `select-therapist-${clientInput}-date=${availability.date}&start_time=${currentSlotTime.start_time_string}&end_time=${currentSlotTime.end_time_string}&room_id=${availability.room.id}`
              "
              search
              prefixIcon="user"
              v-model="therapistInput"
              :placeholder="
                clientInput
                  ? 'Select therapist'
                  : 'Choose the client first to enable therapist selection.'
              "
              :options="therapistOptions"
              :disabled="!clientInput"
              @change="onChangeTherapist"
              :searching="therapistLoading"
              @search="fetchTherapists"
            />
          </div>
          <div class="grid grid-cols-4 gap-2">
            <div
              v-for="slot in timeslotOptions"
              :key="slot.id"
              class="h-10 flex items-center justify-center flex-shrink-0 rounded border border-slate-400 transition-all duration-300"
              :class="[
                timeInput.includes(slot.start_time_string)
                  ? 'bg-light-purple'
                  : 'bg-pure-white',
                slot.appointment_id
                  ? 'cursor-default bg-slate-200'
                  : 'cursor-pointer'
              ]"
              @click="onToggleTime(slot)"
              :title="
                slot.appointment_id
                  ? 'Booked'
                  : `${slot.start_time_string} - ${slot.end_time_string}`
              "
            >
              <BlTextBody2
                :text="`${slot.start_time_string} - ${slot.end_time_string}`"
                class="flex-shrink-0 transition-all duration-300"
                :class="[
                  timeInput.includes(slot.start_time_string)
                    ? 'text-pure-white'
                    : 'text-slate-1000',
                  slot.appointment_id ? 'text-slate-600' : ''
                ]"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        class="w-152 py-10 px-10 flex flex-col bg-pure-white rounded-3xl border border-slate-400"
      >
        <div class="mb-3 flex flex-col gap-1">
          <BlTextSubtitle1 text="Select credits for this appointment" />
          <BlTextBody2
            text="Select the source of credits to apply for this appointment."
          />
        </div>
        <div class="flex flex-col gap-4">
          <div>
            <BlSelectBox
              :key="
                `select-package-item-${clientInput}-${therapistInput}-${timeInput.length}`
              "
              search
              prefixIcon="piggy"
              v-model="packageItemIdInput"
              :placeholder="creditLoading ? 'Loading ...' : 'Select item'"
              :options="packageItemOptions"
              :disabled="!clientInput || !therapistInput"
              @change="packageItemIdInput = $event"
            />
          </div>
          <div class="py-2 px-3 flex flex-col gap-2 bg-cornflower-200 rounded">
            <div class="flex items-center gap-2">
              <BlIcon icon="piggy" color="cornflower-8" class="h-6 w-6" />
              <BlTextSubtitle3
                text="Credits calculation"
                class="text-cornflower-800"
              />
            </div>
            <div class="flex flex-col gap-2">
              <BlTextBody2
                text="Credits will be deducted only after the session ends. However, please be aware of the remaining credits available when creating the appointment."
                class="text-cornflower-800"
              />
              <div class="flex flex-col gap-1">
                <div class="flex items-center justify-between gap-2">
                  <BlTextBody2
                    text="Credits used for this appointment(s)"
                    class="text-cornflower-800"
                  />
                  <div
                    class="rounded h-5.5 w-11 bg-cornflower-800 flex items-center justify-center"
                  >
                    <BlTextSubtitle3
                      :text="creditsUsed"
                      class="text-cornflower-200"
                    />
                  </div>
                </div>
                <div class="flex items-center justify-between gap-2">
                  <BlTextBody2
                    text="Projected remaining credits after this session(s) is completed"
                    class="text-cornflower-800"
                  />
                  <div
                    class="rounded h-5.5 w-11 bg-cornflower-800 flex items-center justify-center"
                  >
                    <BlTextSubtitle3
                      :text="remainingCredits"
                      class="text-cornflower-200"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="packageItemIdInput && remainingCredits < 0"
            class="rounded flex items-start gap-2 py-2 px-3 bg-tulip-100"
          >
            <BlIcon icon="warning" color="tulip-7" class="h-6 w-6" />
            <div class="flex flex-col w-128">
              <BlTextBody2
                text="The credits available after this appointment will be negative. Please add credits to the client's account before creating the appointment."
                class="text-tulip-700"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        class="w-152 py-10 px-10 flex flex-col bg-pure-white rounded-3xl border border-slate-400"
      >
        <div
          class="flex items-center mb-2 cursor-pointer"
          @click="setAssignSupervisorsInput = !setAssignSupervisorsInput"
        >
          <div :style="{ transform: 'scale(1.375)' }" class="pl-2 pr-4">
            <BlInputToggle
              :checked="setAssignSupervisorsInput"
              @changed="setAssignSupervisorsInput = !setAssignSupervisorsInput"
            />
          </div>
          <BlTextSubtitle1 text="Assign supervisor" />
        </div>
        <BlTextBody2
          class="mb-4"
          text="If you assign a supervisor, they will be able to view this appointment on their Sessions page without affecting their appointments and availability slots."
        />
        <div v-if="setAssignSupervisorsInput" class="flex">
          <BlSearchBox
            :key="`select-assign-supervisors-${clientInput}-${therapistInput}`"
            type="user"
            v-model="assignSupervisorsInput"
            placeholder="Select therapist"
            :options="assignSupervisorOptions"
            drop="up"
            @change="assignSupervisorsInput = $event"
            :searching="assignSupervisorsLoading"
            @search="fetchSupervisors"
          />
        </div>
      </div>
    </div>

    <div
      class="sticky bottom-0 z-10 h-20 px-6 flex items-center justify-between flex-shrink-0 bg-pure-white border-t border-slate-400"
    >
      <div class="flex flex-col gap-2">
        <div class="h-1 w-20 bg-light-purple rounded"></div>
        <div class="flex items-center gap-1">
          <div
            class="h-6 w-6 grid place-items-center flex-shrink-0 rounded-full border"
            :class="[
              isDisabledSubmit ? 'border-slate-600' : 'border-success-400'
            ]"
          >
            <BlTextSubtitle4 v-if="isDisabledSubmit" text="1" />
            <BlIcon v-else icon="check" color="success-4" class="h-6 w-6" />
          </div>
          <BlTextSubtitle4 text="Create a new appointment" />
        </div>
      </div>
      <BlInputButton
        label="Book 1 appointment"
        :disabled="isDisabledSubmit"
        :loading="submitLoading"
        @clicked="onSubmit"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { displayDate } from "@/plugins/helper.js";
import { CLOCK_HALF } from "@/plugins/assets.js";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextSubtitle4 from "../../BlTextSubtitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlBreadcrumbs from "../../BlBreadcrumbs.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlSearchBox from "../../BlSearchBox.vue";

export default {
  name: "NewSingleAppointmentOverlay",
  components: {
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextSubtitle3,
    BlTextSubtitle4,
    BlTextBody2,
    BlTextBody3,
    BlBreadcrumbs,
    BlIcon,
    BlInputButton,
    BlInputToggle,
    BlSelectBox,
    BlSearchBox
  },
  props: {
    availability: { type: Object },
    slots: { type: Array, default: () => [] }
    // $emit('close-modal')
    // $emit('after-commit')
  },
  data() {
    return {
      submitLoading: false,
      //
      clientInput: 0,
      clientOptions: [],
      clientLoading: false,
      therapistInput: 0,
      therapistOptions: [],
      therapistLoading: false,
      timeInput: [],
      //
      creditLoading: false,
      clientCreditsData: null,
      packageItemIdInput: null,
      //
      setAssignSupervisorsInput: false,
      assignSupervisorsInput: [],
      assignSupervisorOptions: [],
      assignSupervisorsLoading: false,
      clockHalf: CLOCK_HALF
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "user"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    breadcrumbs() {
      let currentLabel = "Open room";
      if (!this.isOpenRoom) {
        currentLabel = this.availability.user.name;
      }
      const action = () => this.$emit("close-modal");
      return [
        { label: "Appointments", action },
        { label: currentLabel, action, truncate: true }
      ];
    },
    //
    isOpenRoom() {
      return !this.availability.user.id;
    },
    timeslotOptions() {
      return this.availability.slots;
    },
    currentSlotTime() {
      if (!this.timeInput.length) {
        return { start_time_string: "00:00", end_time_string: "00:00" };
      }
      const start = this.timeInput[0];
      const idx = this.clockHalf.indexOf(
        this.timeInput[this.timeInput.length - 1]
      );
      const end = this.clockHalf[idx + 1];
      return { start_time_string: start, end_time_string: end };
    },
    packageItemOptions() {
      const nullOption = {
        value: -1,
        label: `No credits available. Please add credits first to create an appointment.`,
        disabled: true
      };
      if (!this.clientCreditsData) return [nullOption];
      const availableItems = this.clientCreditsData.available_items || [];
      if (!availableItems.length) return [nullOption];
      const arr = [];
      availableItems.forEach(i => {
        const creditTransactions = i.credit_transactions || [];
        // const totalAvailableCredits = creditTransactions.reduce(
        //   (sum, i) => (sum += i.total_available_credits),
        //   0
        // );
        // const totalReservedCredits = creditTransactions.reduce(
        //   (sum, i) => (sum += i.total_reserved_credits),
        //   0
        // );
        const totalUnusedCredits = creditTransactions.reduce(
          (sum, i) => (sum += i.total_unused_credits),
          0
        );

        const remaining = totalUnusedCredits;
        const isAvailable = i.is_available;
        const suffix = !isAvailable
          ? "Unavailable for this therapist"
          : `${remaining} credit(s) available`;
        const disabled = !isAvailable || remaining < this.creditsUsed;

        arr.push({
          ...i,
          total_unused_credits: totalUnusedCredits,
          value: i.package_item_id,
          label: i.package_item_name,
          suffix,
          disabled
        });
      });
      return arr;
    },
    packageItemInputData() {
      if (!this.packageItemIdInput) return null;
      const found = this.packageItemOptions.find(
        i => i.value === this.packageItemIdInput
      );
      if (!found) return null;
      return found;
    },
    //
    creditsUsed() {
      return this.timeInput.length;
    },
    remainingCredits() {
      if (!this.clientInput) return 0;
      if (!this.packageItemInputData) return 0;
      const remaining = this.packageItemInputData.total_unused_credits;
      return remaining - this.creditsUsed;
    },
    isDisabledSubmit() {
      if (!this.clientInput) return true;
      if (!this.therapistInput) return true;
      if (!this.timeInput.length) return true;
      if (!this.packageItemIdInput) return true;
      if (this.packageItemIdInput && this.remainingCredits < 0) return true;
      return false;
    }
  },
  watch: {
    therapistInput: {
      immediate: true,
      handler(val) {
        this.getUser({ id: val });
      }
    }
  },
  mounted() {
    const userId = this.availability.user.id || 0;
    this.therapistInput = userId;
    this.timeInput = this.slots.map(i => i.start_time_string);
  },
  methods: {
    ...mapActions([
      "actionGet",
      "getUser",
      "getClient",
      "getSupervisors",
      "createRecurringAppointments"
    ]),
    displayDate,
    //
    async onChangeClient(val) {
      this.clientInput = val;
      this.clientCreditsData = null;
      this.packageItemIdInput = null;

      if (this.isOpenRoom) {
        this.therapistInput = 0;
        return;
      }

      const userId = this.therapistInput || this.user.id;
      this.fetchAvailbleCredits(val, userId);
    },
    async onChangeTherapist(val) {
      this.therapistInput = val;
      this.clientCreditsData = null;
      this.packageItemIdInput = null;

      this.fetchAvailbleCredits(this.clientInput, val);
    },
    //
    async fetchClients(query) {
      const userId = this.therapistInput;
      const date = this.availability.date;
      const start = this.currentSlotTime.start_time_string;
      const end = this.currentSlotTime.end_time_string;

      const params = {
        url: `/api/v1/clients/check_appointment?sort=name_asc&date=${date}&start_time=${start}&end_time=${end}`
      };
      if (userId) params.url += `&user_id=${userId}`;
      if (query) params.url += `&query=${query}`;

      this.clientLoading = true;
      const { success, data } = await this.actionGet(params);
      this.clientLoading = false;
      if (!success) return;

      const arr = data.map(i => {
        return {
          ...i,
          value: i.id,
          label: i.name,
          disabled: i.is_booked,
          suffix: i.is_booked ? "Booked" : ""
        };
      });
      this.clientOptions = [...this.clientOptions, ...arr];
    },
    async fetchAvailbleCredits(clientId, userId) {
      this.clientCreditsData = null;
      const params = {
        url: `/api/v1/clients/${clientId}/credit_transaction_details?user_id=${userId}`
      };

      this.creditLoading = true;
      const { success, data } = await this.actionGet(params);
      this.creditLoading = false;
      if (!success) return;

      this.clientCreditsData = data;
    },
    async fetchTherapists(query) {
      let params = `?sort=alphabetical&status=active&date=${this.availability.date}&start_time=${this.currentSlotTime.start_time_string}&end_time=${this.currentSlotTime.end_time_string}&room_id=${this.availability.room.id}`;
      if (this.clientInput) params += `&client_id=${this.clientInput}`;
      if (query) params += `&query=${query}`;

      this.therapistLoading = true;
      const { success, data } = await this.getSupervisors({ params });
      this.therapistLoading = false;
      if (!success) return;

      this.therapistOptions = data.map(i => ({
        ...i,
        value: i.id,
        label: i.name,
        disabled: !i.is_available,
        suffix: !i.is_available ? "Unavailable" : ""
      }));
    },
    async fetchSupervisors(query) {
      let params = `?sort=alphabetical&status=active`;
      if (query) params += `&query=${query}`;
      if (this.clientInput) params += `&client_id=${this.clientInput}`;

      this.assignSupervisorsLoading = true;
      const { success, data } = await this.getSupervisors({ params });
      this.assignSupervisorsLoading = false;
      if (!success) return;

      this.assignSupervisorOptions = data
        .map(i => ({ ...i, value: i.id, label: i.name }))
        .filter(i => i.id !== this.therapistInput);
    },
    //
    onToggleTime(slot) {
      const disabled = slot.appointment_id;
      const value = slot.start_time_string;
      if (disabled) return;

      let arr = [...this.timeInput];
      const clocks = this.clockHalf;
      const options = this.timeslotOptions;

      const remove = arr.includes(value);
      if (remove) {
        arr = arr.filter(i => i !== value);
      } else {
        arr.push(value);
      }
      arr.sort((a, b) => clocks.indexOf(a) - clocks.indexOf(b));
      if (arr.length > 1) {
        const firstIndex = clocks.indexOf(arr[0]);
        const lastIdx = clocks.indexOf(arr[arr.length - 1]);
        const valueIdx = clocks.indexOf(value);
        const currentIndex = lastIdx < valueIdx ? lastIdx : valueIdx;
        arr = clocks.filter(
          (_, idx) => idx >= firstIndex && idx <= currentIndex
        );
      }
      let hasDisabled = false;
      arr.forEach(i => {
        const opt = options.find(o => o.start_time_string === i);
        if (!opt) hasDisabled = true;
        else if (opt.appointment_id) hasDisabled = true;
      });
      if (hasDisabled) {
        arr = [value];
      }
      this.timeInput = arr;
    },
    //
    async onSubmit() {
      const appointment = {
        client_id: this.clientInput,
        user_id: this.therapistInput,
        room_id: this.availability.room.id,
        start_date: this.availability.date,
        start_time: this.currentSlotTime.start_time_string,
        end_time: this.currentSlotTime.end_time_string,
        supervisor_ids: [],
        package_item_id: this.packageItemIdInput
      };
      if (this.setAssignSupervisorsInput) {
        appointment.supervisor_ids = this.assignSupervisorsInput;
      }

      const params = {
        params: {
          recurring_appointment: appointment
        },
        title: null,
        message: null
      };

      this.submitLoading = true;
      const { success } = await this.createRecurringAppointments(params);
      if (!success) {
        this.submitLoading = false;
        return;
      }
      setTimeout(() => {
        this.submitLoading = false;
        this.$emit("after-commit");
        this.$emit("close-modal");
      }, 1000);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/overlay/NewSingleAppointmentOverlay.vue